// El mensaje que se le enseña al usuario cuando una llamada al servidor falla.
//
// Con axios, `e.message` es SIEMPRE "Request failed with status code 500": el mensaje que el servidor
// se ha molestado en escribir viaja en `e.response.data.error` y se estaba tirando a la basura en
// decenas de sitios. El patrón bueno ya se usaba en 400 sitios; esto lo pone en uno solo para que no
// vuelvan a divergir.
//
// Uso: `catch (e) { toast.error(msgError(e)) }`
// Con un texto propio de respaldo: `toast.error(msgError(e, 'No se pudo guardar el presupuesto'))`

import { t } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export function msgError(e, respaldo) {
  // 0. LO QUE NI SIQUIERA HA SALIDO DEL NAVEGADOR. `services/api` para en seco las peticiones de una
  // clínica visitada cuyo acceso ya se ha terminado (se habrían guardado en la clínica propia). No
  // hay respuesta del servidor que leer, y sin esta línea el resto de la función acabaría diciendo
  // "Sin conexión con el servidor", que es una avería inventada: no ha fallado ninguna red.
  if (e?.esVisitaTerminada && typeof e.message === 'string' && e.message.trim()) return e.message.trim();
  // t548 · y lo que para por ser una pestaña que se ha quedado en otra clínica (si lo paró el servidor, su texto, abajo).
  if (e?.esPestanaDeOtraClinica && !e?.response && typeof e.message === 'string' && e.message.trim()) return e.message.trim();

  const d = e?.response?.data;

  // 1. Lo que ha escrito el servidor a propósito. Es siempre lo más útil.
  let texto = (typeof d?.error === 'string' && d.error.trim()) ? d.error.trim()
            : (typeof d?.message === 'string' && d.message.trim()) ? d.message.trim()
            : (typeof d === 'string' && d.trim() && !d.trim().startsWith('<')) ? d.trim()
            : null;

  // 2. Sin cuerpo útil: por el código de estado, que al menos orienta.
  if (!texto) {
    const s = e?.response?.status;
    if (s === 401) texto = t('utils.errores.tu_sesion_ha_caducado_vuelve', 'Tu sesión ha caducado. Vuelve a entrar.');
    else if (s === 403) texto = t('utils.errores.no_tienes_permiso_para_hacer', 'No tienes permiso para hacer esto.');
    else if (s === 404) texto = t('utils.errores.eso_ya_no_existe_puede', 'Eso ya no existe. Puede que lo haya borrado otra persona.');
    else if (s === 409) texto = t('utils.errores.alguien_ha_cambiado_esto_mientras', 'Alguien ha cambiado esto mientras lo editabas. Refresca y vuelve a intentarlo.');
    else if (s === 413) texto = t('utils.errores.el_archivo_es_demasiado_grande', 'El archivo es demasiado grande.');
    else if (s === 429) texto = t('utils.errores.demasiados_intentos_seguidos_espera_unos', 'Demasiados intentos seguidos. Espera unos segundos.');
    else if (s === 503) texto = t('utils.errores.esta_parte_no_esta_disponible', 'Esta parte no está disponible ahora mismo.');
    else if (s >= 500) texto = t('utils.errores.el_servidor_ha_fallado_vuelve', 'El servidor ha fallado. Vuelve a intentarlo en unos segundos.');
    // Sin respuesta ninguna: o no hay red, o el servidor no contesta.
    else if (e?.code === 'ECONNABORTED') texto = t('utils.errores.la_operacion_ha_tardado_demasiado', 'La operación ha tardado demasiado. Vuelve a intentarlo.');
    // B-921 · SOLO SI DE VERDAD HA SALIDO. Un fallo de la propia pantalla antes de enviar (un TypeError, un cuerpo que no
    // se puede convertir a texto) tampoco trae respuesta, y se contaba como «Sin conexión»: una avería inventada que
    // manda a mirar la red. Sale sin respuesta de verdad una petición de axios (isAxiosError, request) o un fetch que
    // no llegó («Failed to fetch», «NetworkError…», «Load failed» en Safari); lo demás se queda con el texto de respaldo.
    else if (!e?.response && (e?.isAxiosError || e?.request || /failed to fetch|networkerror|load failed/i.test(String(e?.message || '')))) texto = t('utils.errores.sin_conexion_con_el_servidor', 'Sin conexión con el servidor. Comprueba tu red.');
  }

  if (!texto) texto = respaldo || t('utils.errores.no_se_ha_podido_completar', 'No se ha podido completar la operación.');

  // La referencia que devuelve el saneador del servidor: permite encontrar el fallo exacto en el log
  // cuando el usuario lo cuenta por teléfono. Solo se añade si el servidor la manda.
  if (d?.ref) texto += ` (ref. ${d.ref})`;
  return texto;
}

export default msgError
